import type { BookingTranslateFn } from '../i18n/bookingFormLabels';
import { bookingRecordAssetId } from './bookingListDisplay';
import { bookingRecordIsTerminal, bookingRecordPrimaryId } from './bookingRecordMutate';

function coerceCapacityNumber(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  if (typeof raw === 'string' && raw.trim()) {
    const n = Number(raw.trim());
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

export function assetRecordMaxCapacity(asset: Record<string, unknown>): number | null {
  const max = coerceCapacityNumber(
    asset.max_capacity ??
      asset.maxCapacity ??
      asset.capacity ??
      asset.max_people ??
      asset.maxPeople,
  );
  if (max === null || max < 1) {
    return null;
  }
  return Math.floor(max);
}

export function bookingRecordPeopleCount(booking: Record<string, unknown>): number {
  const raw = booking.amount_of_people;
  if (typeof raw === 'number' && Number.isFinite(raw) && raw >= 1) {
    return Math.floor(raw);
  }
  if (typeof raw === 'string' && raw.trim()) {
    const n = Number(raw.trim());
    if (Number.isFinite(n) && n >= 1) {
      return Math.floor(n);
    }
  }
  return 1;
}

function parseIsoMs(iso: string): number | null {
  const ms = new Date(iso.trim()).getTime();
  return Number.isNaN(ms) ? null : ms;
}

function intervalsOverlap(startA: number, endA: number, startB: number, endB: number): boolean {
  return startA < endB && startB < endA;
}

export function sumOverlappingBookingPeople(params: {
  bookings: Record<string, unknown>[];
  assetId: string;
  startIso: string;
  endIso: string;
  excludeBookingId?: string | null;
}): number {
  const want = params.assetId.trim().toLowerCase();
  const startMs = parseIsoMs(params.startIso);
  const endMs = parseIsoMs(params.endIso);
  if (!want || startMs === null || endMs === null || endMs <= startMs) {
    return 0;
  }
  const exclude = params.excludeBookingId?.trim().toLowerCase() ?? null;
  let sum = 0;
  for (const booking of params.bookings) {
    if (bookingRecordIsTerminal(booking)) {
      continue;
    }
    const bid = bookingRecordAssetId(booking);
    if (!bid || bid.toLowerCase() !== want) {
      continue;
    }
    if (exclude) {
      const bookingId = bookingRecordPrimaryId(booking)?.toLowerCase() ?? null;
      if (bookingId === exclude) {
        continue;
      }
    }
    const bStart = booking.start_date;
    const bEnd = booking.end_date;
    if (typeof bStart !== 'string' || typeof bEnd !== 'string') {
      continue;
    }
    const bStartMs = parseIsoMs(bStart);
    const bEndMs = parseIsoMs(bEnd);
    if (bStartMs === null || bEndMs === null || bEndMs <= bStartMs) {
      continue;
    }
    if (intervalsOverlap(startMs, endMs, bStartMs, bEndMs)) {
      sum += bookingRecordPeopleCount(booking);
    }
  }
  return sum;
}

export function availableBookingPeopleCapacity(params: {
  asset: Record<string, unknown>;
  bookings: Record<string, unknown>[];
  assetId: string;
  startIso: string;
  endIso: string;
  excludeBookingId?: string | null;
}): number | null {
  const max = assetRecordMaxCapacity(params.asset);
  if (max === null) {
    return null;
  }
  const used = sumOverlappingBookingPeople(params);
  return Math.max(0, max - used);
}

export type BookingPeopleCapacityValidateParams = {
  asset: Record<string, unknown>;
  bookings: Record<string, unknown>[];
  assetId: string;
  startIso: string;
  endIso: string;
  requestedPeople: number;
  excludeBookingId?: string | null;
};

/**
 * When the requested headcount exceeds remaining capacity for the interval,
 * returns the available capacity (for localized error copy). Otherwise `null`.
 */
export function validateBookingPeopleCapacity(
  params: BookingPeopleCapacityValidateParams,
): number | null {
  const requested = Math.floor(params.requestedPeople);
  if (!Number.isFinite(requested) || requested < 1) {
    return 0;
  }
  const available = availableBookingPeopleCapacity(params);
  if (available === null) {
    return null;
  }
  if (requested > available) {
    return available;
  }
  return null;
}

const PEOPLE_CAPACITY_API_RE =
  /^Requested amount of people exceeds available capacity of (\d+) for the selected dates\.?$/i;

/** Map English API capacity errors to localized UI text. */
export function localizeBookingPeopleCapacityError(message: string, t: BookingTranslateFn): string {
  const trimmed = message.trim();
  const match = PEOPLE_CAPACITY_API_RE.exec(trimmed);
  if (match) {
    return t('bookings.error.peopleExceedsCapacity', { capacity: match[1] });
  }
  return message;
}
